import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../../lib/cn';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Raises the shadow. For the score panel, which sits above the form. */
  elevated?: boolean;
  /** Adds a lift on hover. For cards that are themselves clickable. */
  interactive?: boolean;
}

export function Card({
  className,
  elevated = false,
  interactive = false,
  children,
  ...props
}: CardProps) {
  return (
    <div
      className={cn(
        // `relative` is for the gradient top edge below.
        'relative rounded-card border border-line bg-card',
        elevated ? 'shadow-lifted' : 'shadow-card',
        interactive && 'hover-lift',
        className
      )}
      {...props}
    >
      {/* A streak of gradient along the top of an elevated card — decorative, and
          a sibling rather than a border because `border-image` and
          `border-radius` do not cooperate.

          Inset by 2rem, which is more than the 1.5rem corner radius, so it stays
          on the flat part of the edge. Running it full-width would need
          `overflow-hidden` on the card, and that would clip the score ring's
          glow. */}
      {elevated ? (
        <span
          aria-hidden="true"
          className="absolute inset-x-8 top-0 h-0.5 rounded-pill"
          style={{ backgroundImage: 'var(--gradient-cta)' }}
        />
      ) : null}
      {children}
    </div>
  );
}

export interface CardHeaderProps {
  title: ReactNode;
  /** One line of context. Skip it rather than padding it out. */
  description?: ReactNode;
  /** Right-aligned slot for a score pip, a count, or an action. */
  aside?: ReactNode;
  /** Heading level, so the page keeps a sane document outline. */
  as?: 'h2' | 'h3' | 'h4';
  id?: string;
  className?: string;
}

export function CardHeader({
  title,
  description,
  aside,
  as: Heading = 'h3',
  id,
  className,
}: CardHeaderProps) {
  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <div className="min-w-0">
        <Heading id={id} className="text-base font-semibold text-strong">
          {title}
        </Heading>
        {description ? <p className="mt-1 text-sm text-muted">{description}</p> : null}
      </div>
      {aside ? <div className="shrink-0">{aside}</div> : null}
    </div>
  );
}

/** Standard inner padding. Separate so a card can opt out for a flush list. */
export function CardBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('p-5 sm:p-6', className)} {...props} />;
}
